import { Manager, Socket as SocketIOClient } from 'socket.io-client';
import { ConnectedAdmin, IBreakoutRoomProfile } from "../../types/working-model";
import { assertString } from './assertions';
import { LeakyBucket } from "./leaky-bucket";
import { GetSocketOptions, IncomingTypes, SocketMessage } from "./types";

assertString(process.env.REACT_APP_SOCKET_HOST);

class Man {
	protected options = { transports: ['websocket'], multiplex: true };
	public man: Manager | null = null;
	public connected = false;
	private host?: string;

	get = (reconnect = false, host = process.env.REACT_APP_SOCKET_HOST): Manager => {
		this.host = host;

		if (!this.man) {
			this.man = new Manager(this.host, this.options);
			this.connected = true;
		}

		if (!this.connected && reconnect) {
			this.refresh();
			return this.man as Manager;
		} else {
			return this.man as Manager;
		}
	};

	end = () => {
		if (this.man) {
			this.man.engine.close();
			this.man = null;
			this.connected = false;
		}
	};

	refresh = () => {
		this.man = new Manager(this.host, this.options);
		this.connected = true;
	};
}

const man = new Man();
export class Socket {
	private breakoutProfile?: IBreakoutRoomProfile;
	private commentBucket: LeakyBucket;
	private connectedAdmin?: ConnectedAdmin;
	public connection: SocketIOClient;
	private ns: string;
	private path: string;
	public waitingMessages: SocketMessage[] = [];
	public disconnected = false;
	public onCurrentlyWatching?: (viewers: string) => void;
	public ready = false;
	public gettingConnection = true;
	private heartbeatTimeout: NodeJS.Timeout;
	private receivedMessages = new Set<string>();
	private disconnectTimestamp = 0;
	private manager = man;
	private hostname = process.env.REACT_APP_SOCKET_HOST;

	constructor(ns: string, options?: GetSocketOptions) {
		this.ns = ns;
		this.path = `/${ns}`;

		if (options?.chat) {
			this.hostname = process.env.REACT_APP_CHAT_SOCKET_HOST;
		}

		if (options?.transcripts) {
			this.hostname = process.env.REACT_APP_TRANSCRIPTS_HOST;
		}

		this.connection = this.getManager().socket(this.path);
		this.setupListeners();
		this.commentBucket = new LeakyBucket(options?.rateLimitMs ?? 1000, ns);
		this.connectedAdmin = options?.admin;
		this.breakoutProfile = options?.profile;
		this.heartbeatTimeout = setInterval(() => {
			this.heartbeat();
		}, 60_000);
	}

	private getManager = () => {
		return this.manager.get(
			true,
			this.hostname
		);
	};

	private setupListeners = () => {
		this.connection.on('connect', this.handleConnection);
		this.connection.on('message', this.handleMessage);
		this.connection.on('connecting', this.logEvent('connecting'));
		this.connection.on('connect_error', this.handleConnectError);
		this.connection.on('connect_timeout', this.connectTimeout('connect timeout'));
		this.connection.on('disconnect', this.handleDisconnect);
		this.connection.on('reconnect', this.logEvent('reconnected'));
		this.connection.on('reconnect_attempt', this.logEvent('reconnect attempt'));
		this.connection.on('reconnecting', this.logEvent('reconnecting'));
		this.connection.on('error', this.logEvent('error'));
		this.connection.on('reconnect_failed', this.connectTimeout('reconnect failed'));
	};

	private heartbeat = () => {
		const user = this.connectedAdmin || this.breakoutProfile;
		if (user && this.connection.connected) {
			this.connection.emit('heartbeat', user);
		}
	};

	private connectTimeout = (reason: string) => {
		return (e: any) => {
			console.error(`Socket ${reason}, info: ${e?.toString()}`);
			this.connection.removeAllListeners();
			this.connection.close();

			setTimeout(() => {
				this.connection = this.getManager().socket(this.path);
				this.setupListeners();
			}, 1000);
		};
	};

	private logEvent = (event: string) => {
		return () => {
			console.warn(`Socket ${event} ${this.ns}`);
		};
	};

	// triggered when connection to socket fails to be established initially
	// OR when the socket connection drops unexpectedly
	private handleConnectError = (e: Error): void => {
		if (!this.gettingConnection) {
			console.warn("Socket connection lost, going to reconnect", e);
		} else {
			console.error("Socket connection error", e);
		}

		this.disconnectTimestamp = Date.now();
	};

	private isTranscripts = () => {
		return this.hostname = process.env.REACT_APP_TRANSCRIPTS_HOST;
	};

	private getAfterTimestampForBacklog = (): number => {
		if (this.disconnectTimestamp) {
			return this.disconnectTimestamp;
		}

		return Date.now() - 1000 * 10; // last 10 seconds
	};

	private handleConnection = (): void => {
		this.gettingConnection = false;

		if (!this.ready) {
			this.ready = true;
			this.setConnectedUser();

			//if we have messages queued up before the connection is ready, fire them all now that we have a connection
			if (this.waitingMessages.length > 0) {
				for (const message of this.waitingMessages) {
					this.connection.emit('message', message);
				}

				this.waitingMessages = [];
			}
		}

		if (this.disconnectTimestamp) {
			this.requestBacklog(this.getAfterTimestampForBacklog());
			this.disconnectTimestamp = 0;
		}
	};

	public requestBacklog = (after: number): void => {
		if (this.connection.connected) {
			this.connection.once('backlog', (messages: SocketMessage[]) => {
				messages.forEach(message => this.handleMessage(message, true));
			});
			this.connection.emit('get-backlog', after);
		}
	};

	private postMessage = (message: any) => {
		self.postMessage({ ...message, _ns: this.ns });
	};

	private handleError = (e: Error): void => {
		console.error(e);
	};

	private handleDisconnect = (): void => {
		console.warn('Disconnected socket');
		this.ready = false;
	};

	public setConnectedUser = (): void => {
		if (this.connectedAdmin || this.breakoutProfile) {
			if (this.connectedAdmin) {
				this.connection.on(`user-connected`, this.handleConnectedAdmin);
				this.connection.on(`users-connected`, this.handleAdminsConnected);
				this.connection.on(`user-disconnected`, this.handleDisconnectedAdmin);
				this.connection.on(`user-updated`, this.handleConnectedAdmin);
				this.connection.emit('set-user', this.connectedAdmin);
			} else {
				this.connection.on('users-connected', this.handleUpdatedBRProfile);
				this.connection.on('user-connected', this.handleConnectedBRProfile);
				this.connection.on('user-disconnected', this.handleDisconnectedBRProfile);
				this.connection.on('user-updated', this.handleBRProfilesConnected);
				this.connection.emit('set-user', this.breakoutProfile);
			}

			setTimeout(() => {
				this.connection.emit('get-users');
			}, 1000);
		}
	};

	public setConnectedBreakout = (profile: IBreakoutRoomProfile): void => {
		this.breakoutProfile = profile;
		this.connection.emit('set-user', profile);
		setTimeout(() => {
			this.connection.emit('get-users');
		}, 1000);
	};

	public getCurrentlyWatching = (callback: (viewers: { viewers: string; }) => void): void => {
		this.connection.once('currently-watching', callback);
		this.connection.emit('get-currently-watching');
	};

	public updateWorkingEvent = (admin: ConnectedAdmin): void => {
		this.connectedAdmin = admin;
		this.connection.emit('update-user', admin);
	};

	public handleUpdatedUser = (admin: ConnectedAdmin): void => {
		this.postMessage({ type: IncomingTypes.handleUpdatedUser, admin });
	};

	public handleConnectedAdmin = (admin: ConnectedAdmin): void => {
		this.postMessage({ type: IncomingTypes.handleConnectedAdmin, admin });
	};

	public handleDisconnectedAdmin = (admin: ConnectedAdmin): void => {
		this.postMessage({ type: IncomingTypes.handleDisconnectedAdmin, admin });
	};

	public handleAdminsConnected = (admins: ConnectedAdmin[]): void => {
		this.postMessage({ type: IncomingTypes.handleAdminsConnected, admins });
	};

	public handleUpdatedBRProfile = (blProfiles: IBreakoutRoomProfile[]): void => {
		this.postMessage({ type: IncomingTypes.handleUpdatedBRProfile, blProfiles });
	};

	public handleConnectedBRProfile = (blProfile: IBreakoutRoomProfile): void => {
		this.postMessage({ type: IncomingTypes.handleConnectedBRProfile, blProfile });
	};

	public handleDisconnectedBRProfile = (blProfile: IBreakoutRoomProfile): void => {
		this.postMessage({ type: IncomingTypes.handleDisconnectedBRProfile, blProfile });
	};

	public handleBRProfilesConnected = (blProfiles: IBreakoutRoomProfile[]): void => {
		this.postMessage({ type: IncomingTypes.handleBRProfilesConnected, blProfiles });
	};

	public handleMessage = async (message: SocketMessage, fromBacklog?: boolean): Promise<void> => {
		// prevent dup messages from being processed on reconnect
		if (message.id && this.receivedMessages.has(message.id)) return;

		if (message.chat_messages) {
			this.commentBucket.add({ type: IncomingTypes.message, message });
		} else {
			this.postMessage({ type: IncomingTypes.message, message, fromBacklog });
		}

		// if message has an id, save it so we don't process it again on reconnect
		if (message.id) {
			this.receivedMessages.add(message.id);
		}
	};

	public sendMessage = (message: SocketMessage): void => {
		if (!this.ready) {
			this.waitingMessages.push(message);
		} else {
			this.connection.emit('message', message);
		}
	};

	public sendBreakoutData = (sessionUuid: string, breakoutUuid: string, opentokId: string, host: string): void => {
		this.connection.emit('breakouts-data', { sessionUuid, breakoutUuid, opentokId, host });
	};

	public reconnect = (options?: GetSocketOptions): void => {
		if (!this.manager.connected) {
			this.manager.refresh();
		}

		this.disconnected = false;
		this.setupListeners();
		this.connection.connect();
		this.commentBucket = new LeakyBucket(options?.rateLimitMs ?? 1000, this.ns);
		this.connectedAdmin = options?.admin;
		this.breakoutProfile = options?.profile;
		this.heartbeatTimeout = setTimeout(() => {
			this.heartbeat();
		}, 60_000);
	};

	public disconnect = (): void => {
		console.warn('Disconnecting socket');
		this.disconnectTimestamp = Date.now();
		this.disconnected = true;
		this.ready = false;
		this.connection.removeAllListeners();
		this.commentBucket.stop();
		this.connection.disconnect();
		clearInterval(this.heartbeatTimeout);
	};

	public end = () => {
		this.manager.end();
	};
}
